import React from "react";
import { Helmet } from "react-helmet";
import ImageCarousel from "../UI/ImageCarousel";
import "./LandingPage.css";
import Navbar from "../UI/Navbar";
import Footer from "../UI/Footer";
import { useTranslation } from "react-i18next";



const LandingPage = () => {
  const { t, i18n } = useTranslation('landing');
  const currentLang = i18n.language;
  
  /* -------------------- Structured Data -------------------- */
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "AiVatar - AI Avatar & Headshot Generator",
    description:
      "Create personalised AI avatars and headshots from a handful of photos. Extensive style catalogue, instant downloads, commercial-use ready.",
    image: "https://aivatar.muware.xyz/og-image.webp",
    brand: {
      "@type": "Brand",
      name: "AiVatar"
    },
    aggregateRating:{
      "@type":"AggregateRating",
      "ratingValue":"4.8",
      "reviewCount":"2300"
    },
    /* -------------- OFFERS -------------- */
    offers: [
      /* 40-token lifetime pack */
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "40-token lifetime pack",
        description: "One-off pack of 40 tokens (~10-20 avatars).",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "USD",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "40-token lifetime pack",
        description: "One-off pack of 40 tokens (~10-20 avatars).",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },

      /* 100-token lifetime pack */
      {
        "@type": "Offer",
        price: "19",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "100-token lifetime pack",
        description: "One-off pack of 100 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },
      {
        "@type": "Offer",
        price: "19",
        priceCurrency: "USD",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "100-token lifetime pack",
        description: "One-off pack of 100 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },

      /* 200-token lifetime pack */
      {
        "@type": "Offer",
        price: "35",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "200-token lifetime pack",
        description: "One-off pack of 200 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },
      {
        "@type": "Offer",
        price: "35",
        priceCurrency: "USD",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "200-token lifetime pack",
        description: "One-off pack of 200 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },

      /* 500-token lifetime pack */
      {
        "@type": "Offer",
        price: "79",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "500-token lifetime pack",
        description: "One-off pack of 500 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },
      {
        "@type": "Offer",
        price: "79",
        priceCurrency: "USD",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "500-token lifetime pack",
        description: "One-off pack of 500 tokens.",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },

      /* Monthly subscription - 100 tokens/month */
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "Monthly subscription - 100 tokens/month",
        description: "Renews every month; unused tokens expire at month-end.",
        eligibleDuration: "P1M",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      },
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "USD",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "Monthly subscription - 100 tokens/month",
        description: "Renews every month; unused tokens expire at month-end.",
        eligibleDuration: "P1M",
        shippingDetails: { "@id": "#digitalFreeShip" },
        hasMerchantReturnPolicy: { "@id": "#noRefund" }
      }
    ],

    /* ----- Re-utilisable nodes defined once at root ----- */
    shippingDetails: {
      "@id": "#digitalFreeShip",
      "@type": "OfferShippingDetails",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: "0",
        currency: "EUR"
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          value: "0",
          unitCode: "HUR"
        },
        transitTime: {
          "@type": "QuantitativeValue",
          value: "0",
          unitCode: "HUR"
        }
      },
      shippingDestination: {
        "@type": "DefinedRegion",
        addressCountry: ["US", "GB", "CA", "AU", "FR", "DE", "ES", "IT", "JP", "BR", "IN", "RU", "CN"]
      }
    },
    hasMerchantReturnPolicy: {
      "@id": "#noRefund",
      "@type": "MerchantReturnPolicy",
      applicableCountry: ["US", "GB", "CA", "AU", "FR", "DE", "ES", "IT", "JP", "BR", "IN", "RU", "CN"],
      returnPolicyCategory:
        "https://schema.org/MerchantReturnNotPermitted",
      merchantReturnDays: 0
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: t('faq.items.free.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.free.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.photos.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.photos.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.commercial.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.commercial.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.time.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.time.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.styles.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.styles.a')
        }
      }
    ]
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "AiVatar - 60-second Demo",
    description: "Watch how easy it is to create a professional AI avatar from your photos in under a minute.",
    thumbnailUrl: "https://i.ytimg.com/vi_webp/XXXXXXXXXXX/maxresdefault.webp",
    uploadDate: "2025-02-12T08:00:00+00:00",
    contentUrl: "https://www.youtube.com/watch?v=XXXXXXXXXXX",
    embedUrl: "https://www.youtube.com/embed/XXXXXXXXXXX",
    duration: "PT0M59S"
  };

  /* -------------------- JSX -------------------- */
  return (
    <div className="landing-page">

      <Navbar />

      {/* === Above-the-fold visual carousel === */}
      <ImageCarousel />

      <Helmet>
        <html lang={currentLang} />
        <title>{t('seo.title')}</title>
        <meta
          name="description"
          content={t('seo.description')}
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`https://aivatar.muware.xyz${currentLang === 'fr' ? '/fr' : ''}/`} />
        
        {/* Alternate language version for hreflang */}
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/"
          hrefLang="en"
        />
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/fr/"
          hrefLang="fr"
        />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        {/* <script type="application/ld+json">{JSON.stringify(videoSchema)}</script> */}
        
        {/* Open Graph / Twitter Card */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.title')} />
        <meta property="og:description" content={t('seo.description')} />
        <meta property="og:image" content="https://aivatar.muware.xyz/og-image.webp" />
        <meta property="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ---------------- HERO ---------------- */}
      <section className="landing-page__hero section container text-center">
        <h1 className="mb-4 mx-auto" style={{ maxWidth: "800px" }}>
          {t('hero.title')}
        </h1>
        <p
          className="landing-page__subtitle mb-5 mx-auto"
          style={{ maxWidth: "600px" }}
          dangerouslySetInnerHTML={{ __html: t('hero.subtitle') }}
        />
        <a href={`/${currentLang === 'fr' ? 'fr/' : ''}signup`} className="btn btn-primary btn-xl">
          {t('hero.button')}
        </a>
      </section>

      {/* SOCIAL PROOF (metrics) */}
      <section className="landing-page__social-proof text-center">
        <div className="container">
          <span>{t('socialProof.generationTime')}</span>
          <span>{t('socialProof.styles')}</span>
          <span>{t('socialProof.privacy')}</span>
        </div>
      </section>

      {/* NEW - Who is AiVatar for? */}
      <section className="landing-page__section-container">
        <div className="landing-page__panel section text-center">
          <h2 className="mb-5">{t('forWho.title')}</h2>
          <div className="landing-page__features-grid">
            {[
              {
                title: t('forWho.professionals.title'),
                text: t('forWho.professionals.text')
              },
              {
                title: t('forWho.creators.title'),
                text: t('forWho.creators.text')
              },
              {
                title: t('forWho.everyday.title'),
                text: t('forWho.everyday.text')
              }
            ].map((item, i) => (
              <div key={i} className="landing-page__feature-card text-center p-5">
                <h3 className="font-bold mb-3">{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (existing) */}
      <section className="landing-page__section-container">
        <div className="landing-page__how-it-works section text-center">
          <h2 className="mb-6">{t('howItWorks.title')}</h2>
          <div className="landing-page__steps-grid">
            {[
              {
                step: "1",
                title: t('howItWorks.step1.title'),
                text: t('howItWorks.step1.text')
              },
              {
                step: "2",
                title: t('howItWorks.step2.title'),
                text: t('howItWorks.step2.text')
              },
              {
                step: "3",
                title: t('howItWorks.step3.title'),
                text: t('howItWorks.step3.text')
              }
            ].map((item, i) => (
              <div key={i} className="landing-page__step-card">
                <div className="step-number">{item.step}</div>
                <h3 className="font-bold">{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW - 60-second video demo */}
      {/* <section className="landing-page__section-container">
        <div className="section text-center">
          <h2 className="mb-5">{t('videoDemo.title')}</h2>
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
            <iframe
              src="https://www.youtube.com/embed/XXXXXXXXXXX"
              title="AiVatar demo video"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </section> */}

      {/* SHOWCASE (replace placeholders later) */}
      <section className="landing-page__section-container">
        <div className="landing-page__examples-section">
          <h2 className="text-center mb-6">{t('exampleStyles.title')}</h2>
          <p className="landing-page__examples-subtitle text-center mb-6">
            {t('exampleStyles.subtitle')}
          </p>
          <div className="landing-page__popular-styles-grid">
            {[
              { src: "aivatar_Anime.webp", alt: "AiVatar – Anime AI avatar generated from photos" },
              { src: "aivatar_Chibi.webp", alt: "AiVatar – Chibi AI avatar generated from photos" },
              { src: "aivatar_CreativeDirector.webp", alt: "AiVatar – Creative Director AI avatar generated from photos" },
              { src: "aivatar_FinancialStrategist.webp", alt: "AiVatar – Financial Strategist AI avatar generated from photos" },
              { src: "aivatar_ModernExecutive.webp", alt: "AiVatar – Modern Executive AI avatar generated from photos" },
              { src: "avatar32.webp", alt: "AiVatar – Startup Visionary AI avatar generated from photos" },
              { src: "aivatar_SustainabilityAdvisor.webp", alt: "AiVatar – Sustainability Advisor AI avatar generated from photos" },
              { src: "aivatar_Synthwave.webp", alt: "AiVatar – Synthwave AI avatar generated from photos" }
            ].map((style, idx) => (
              <figure key={idx} className="landing-page__popular-style-card">
                <img
                  src={`/images/LP/${style.src}`}
                  alt={style.alt}
                  className="landing-page__popular-style-img"
                  loading="lazy"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES (existing) */}
      <section className="landing-page__section-container">
        <div className="landing-page__features-section">
          <h2 className="text-center mb-6">{t('whyChoose.title')}</h2>
          <div className="landing-page__features-grid">
            {[
              {
                title: t('whyChoose.features.diverse.title'),
                text: t('whyChoose.features.diverse.text')
              },
              {
                title: t('whyChoose.features.fast.title'),
                text: t('whyChoose.features.fast.text')
              },
              {
                title: t('whyChoose.features.rights.title'),
                text: t('whyChoose.features.rights.text')
              },
              {
                title: t('whyChoose.features.transparent.title'),
                text: t('whyChoose.features.transparent.text')
              },
              {
                title: t('whyChoose.features.privacy.title'),
                text: t('whyChoose.features.privacy.text')
              },
              {
                title: t('whyChoose.features.growing.title'),
                text: t('whyChoose.features.growing.text')
              }
            ].map((f, i) => (
              <div key={i} className="landing-page__feature-card text-center p-5">
                <h3 className="font-bold mb-3">{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW - Pricing at a glance */}
      <section className="landing-page__section-container">
        <div className="landing-page__pricing-section text-center">
          <h2 className="mb-5">{t('pricing.title')}</h2>
          <p className="mb-6">{t('pricing.subtitle')}</p>
          <a href={`/${currentLang === 'fr' ? 'fr/' : ''}pricing`} className="btn btn-outline btn-lg">
            {t('pricing.button')}
          </a>
        </div>
      </section>

      {/* NEW - Glossary snippet */}
      <section className="landing-page__section-container">
        <div className="landing-page__what-is-section">
          <h2 className="text-center mb-4">{t('whatIs.title')}</h2>
          <p dangerouslySetInnerHTML={{ __html: t('whatIs.definition') }}></p>
          <ul className="mt-3" style={{ listStyle: "disc", paddingLeft: "1.5rem" }}>
            <li>{t('whatIs.features.digital')}</li>
            <li>{t('whatIs.features.highRes')}</li>
            <li>{t('whatIs.features.rights')}</li>
          </ul>
        </div>
      </section>

      {/* FAQ (existing) */}
      <section className="landing-page__section-container">
        <div className="landing-page__faq-section">
          <h2 className="text-center mb-6">{t('faq.title')}</h2>
          <div className="space-y-4 mx-auto" style={{ maxWidth: "800px" }}>
            {[
              {
                q: t('faq.items.free.q'),
                a: t('faq.items.free.a')
              },
              {
                q: t('faq.items.photos.q'),
                a: t('faq.items.photos.a')
              },
              {
                q: t('faq.items.commercial.q'),
                a: t('faq.items.commercial.a')
              },
              {
                q: t('faq.items.time.q'),
                a: t('faq.items.time.a')
              },
              {
                q: t('faq.items.styles.q'),
                a: t('faq.items.styles.a')
              }
            ].map((item, i) => (
              <details key={i} className="p-4 border rounded-lg">
                <summary className="font-semibold cursor-pointer">{item.q}</summary>
                <p className="mt-2">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="landing-page__section-container">
        <div className="landing-page__final-cta-section text-center">
          <h2 className="mx-auto" style={{ maxWidth: "800px" }}>
            {t('finalCta.title')}
          </h2>
          <a href={`/${currentLang === 'fr' ? 'fr/' : ''}signup`} className="btn btn-secondary btn-xl">
            {t('finalCta.button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
